import React, { useEffect, useId, useRef, useState } from "react";
import { motion, animate, useMotionValue, useMotionValueEvent, useReducedMotion } from "motion/react";
import StudioCanvas from "./StudioCanvas";
import { Button } from "./StudioControls";
import StudioTrifoldPreview from "./StudioTrifoldPreview";

function GoldCorner({ corner }) {
  const id = useId().replace(/:/g, "");
  return <svg data-testid="book-corner" className={`ms-book-corner is-${corner}`} viewBox="0 0 100 100" aria-hidden="true">
    <defs><linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
      <stop stopColor="#73501e"/><stop offset=".22" stopColor="#e4c36e"/><stop offset=".4" stopColor="#fff0af"/>
      <stop offset=".57" stopColor="#b88a36"/><stop offset=".8" stopColor="#ecd083"/><stop offset="1" stopColor="#775220"/>
    </linearGradient></defs>
    <path d="M5 91 Q1 94 3 80 L3 18 Q3 3 18 3 H80 Q94 1 91 5 L73 20 Q58 30 42 25 Q26 24 25 42 Q30 58 20 73 Z"
      fill={`url(#${id})`} stroke="#795621" strokeWidth="1.6"/>
    <path d="M8 77 V21 Q8 8 21 8 H77 M13 61 V24 Q13 13 24 13 H61" fill="none" stroke="#f9e6a1" strokeWidth="1.6"/>
    <g fill="none" stroke="#805b26" strokeWidth="2.7" strokeLinecap="round">
      <path d="M19 69 C31 51 10 54 17 38 C23 24 38 39 27 43 C20 46 19 36 26 35"/>
      <path d="M69 19 C51 31 54 10 38 17 C24 23 39 38 43 27 C46 20 36 19 35 26"/>
      <path d="M16 16 Q28 14 29 23 Q18 29 16 16Z"/>
    </g>
    <g fill="none" stroke="#ffecac" strokeWidth="1.4" strokeLinecap="round" transform="translate(-1 -1)">
      <path d="M19 69 C31 51 10 54 17 38 C23 24 38 39 27 43 C20 46 19 36 26 35"/>
      <path d="M69 19 C51 31 54 10 38 17 C24 23 39 38 43 27 C46 20 36 19 35 26"/>
    </g>
  </svg>;
}

export default function StudioMenuPreview({ doc, content }) {
  if (doc.menuKey === "takeout" && doc.pages.length === 2) {
    return <StudioTrifoldPreview doc={doc} content={content} />;
  }
  return <StudioBookPreview doc={doc} content={content} />;
}

function StudioBookPreview({ doc, content }) {
  const position = useMotionValue(0);
  const [progress, setProgress] = useState(0);
  const [magnification, setMagnification] = useState(1);
  useMotionValueEvent(position, "change", setProgress);
  const navigationAnimation = useRef(null);
  const snapTimer = useRef(null);
  const wheelHandler = useRef(null);
  const touchStart = useRef(null);
  const reducedMotion = useReducedMotion();
  const [size, setSize] = useState({ width: 1000, height: 700 });
  const stage = useRef(null);
  const last = Math.ceil((doc.pages.length - 1) / 2);
  const bounded = Math.max(0, Math.min(progress, last));
  const current = reducedMotion ? Math.round(bounded) : Math.floor(bounded);
  const fraction = reducedMotion ? 0 : bounded - current;
  const turning = fraction > 0.00001 && current < last;
  const start = current === 0 ? 0 : current * 2 - 1;
  const pages = current === 0 ? [doc.pages[0]] : doc.pages.slice(start, start + 2);
  useEffect(() => {
    const measure = () => setSize({ width: stage.current.clientWidth, height: stage.current.clientHeight });
    measure();
    stage.current.parentElement.focus();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(stage.current);
    const root = stage.current.closest(".menu-studio") || stage.current.parentElement;
    const wheel = (event) => { if (!event.target.closest?.(".ms-modal")) wheelHandler.current?.(event); };
    root.addEventListener("wheel", wheel, { passive: false, capture: true });
    return () => { observer?.disconnect(); root.removeEventListener("wheel", wheel, true); navigationAnimation.current?.stop(); clearTimeout(snapTimer.current); };
  }, []);
  const frame = 10;
  const width = Math.max(...doc.pages.map((page) => page.width));
  const height = Math.max(...doc.pages.map((page) => page.height));
  const zoom = magnification * Math.max(.08, Math.min((size.width - 48) / ((width + frame * 2) * 2 + 12), (size.height - 40) / (height + frame * 2)));
  const spineWidth = 12 * zoom;
  const leafWidth = (width + frame * 2) * zoom;
  const leafHeight = (height + frame * 2) * zoom;
  const spreadPages = (index) => index === 0 ? [null, doc.pages[0]] : [doc.pages[index * 2 - 1], doc.pages[index * 2] || null];
  const resting = spreadPages(current);
  const next = turning ? spreadPages(current + 1) : resting;
  const base = turning ? [resting[0], next[1]] : resting;
  const turn = (delta) => {
    clearTimeout(snapTimer.current);
    navigationAnimation.current?.stop();
    const value = position.get();
    const target = Math.max(0, Math.min(last, delta > 0 ? Math.floor(value + .00001) + 1 : Math.ceil(value - .00001) - 1));
    if (reducedMotion) position.set(target);
    else navigationAnimation.current = animate(position, target, { duration: .28, ease: [.22, .65, .3, 1] });
  };
  const scheduleSnap = () => {
    clearTimeout(snapTimer.current);
    snapTimer.current = setTimeout(() => {
      const value = position.get();
      const target = Math.round(value);
      if (Math.abs(target - value) > .25 || value === target) return;
      navigationAnimation.current?.stop();
      if (reducedMotion) position.set(target);
      else navigationAnimation.current = animate(position, target, { type: "spring", stiffness: 240, damping: 30, restDelta: .001, restSpeed: .01 });
    }, 120);
  };
  const scrub = (delta, settle = true) => {
    clearTimeout(snapTimer.current);
    navigationAnimation.current?.stop();
    // Direct, reversible mapping: one complete page turn per 360 scroll pixels.
    position.set(Math.max(0, Math.min(last, position.get() + delta / 360)));
    if (settle) scheduleSnap();
  };
  const scrollBook = (e) => {
    e.preventDefault();
    if (e.ctrlKey || e.metaKey) {
      setMagnification(value => Math.max(.5, Math.min(3, value * Math.exp(-e.deltaY * .008))));
      return;
    }
    const delta = (Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY) * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? size.height : 1);
    scrub(delta);
  };
  wheelHandler.current = scrollBook;
  const sleeve = (page, side) => <div className={`ms-book-sleeve ms-book-side-${side}`} style={{ padding: frame * zoom, borderRadius: 5 * zoom, width: leafWidth, height: leafHeight, boxSizing: "border-box" }}>
    {page && <StudioCanvas doc={doc} page={page} content={content} zoom={zoom} preview />}
    {(side === 0 ? ["tl", "bl"] : ["tr", "br"]).map((corner) => <GoldCorner key={corner} corner={corner} />)}
  </div>;
  const destination = fraction > .5 ? next : resting;
  const centerOf = (pair) => !pair[0] ? -leafWidth / 2 : !pair[1] ? leafWidth / 2 : 0;
  const centered = centerOf(resting) + (centerOf(next) - centerOf(resting)) * fraction;
  return <section className="ms-menu-preview" aria-label="Menu presentation" tabIndex={0}
    onPointerDown={(e) => {
      if (e.pointerType === "touch" && !e.target.closest("button")) {
        touchStart.current = { x: e.clientX, y: e.clientY };
        e.currentTarget.setPointerCapture?.(e.pointerId);
      }
    }}
    onPointerMove={(e) => {
      const start = touchStart.current;
      if (!start) return;
      const dx = start.x - e.clientX, dy = start.y - e.clientY;
      touchStart.current = { x: e.clientX, y: e.clientY };
      scrub(Math.abs(dx) > Math.abs(dy) ? dx : dy, false);
    }}
    onPointerUp={() => { if (touchStart.current) scheduleSnap(); touchStart.current = null; }}
    onPointerCancel={() => { touchStart.current = null; }}
    onKeyDown={(e) => { if (["ArrowLeft", "ArrowRight"].includes(e.key)) { e.preventDefault(); e.stopPropagation(); turn(e.key === "ArrowRight" ? 1 : -1); } }}>
    <div className="ms-book-stage" ref={stage}>
      <motion.div className="ms-menu-book ms-hinged-book" initial={false}
        style={{ x: centered, width: leafWidth * 2 + spineWidth, height: leafHeight }}>
        <motion.div className={`ms-book-spine ${!destination[0] ? "is-cover" : !destination[1] ? "is-back" : ""}`} aria-hidden="true" initial={false}
          animate={{ opacity: 1 }}
          style={{ left: leafWidth - 1, width: spineWidth + 2, height: leafHeight }} />
        {base.map((page, side) => <div className="ms-book-resting-leaf" key={side} style={{ left: side * (leafWidth + spineWidth), width: leafWidth }}>
          {page && sleeve(page, side)}
        </div>)}
        {turning && <motion.div className="ms-book-turning-leaf" data-testid="turning-leaf" aria-hidden="true"
          key={current} style={{ left: leafWidth + spineWidth, width: leafWidth, height: leafHeight, transformOrigin: "left center", rotateY: -180 * fraction, x: -spineWidth * fraction }}>
          <div className="ms-book-leaf-front">{sleeve(resting[1], 1)}</div>
          <div className="ms-book-leaf-back">{sleeve(next[0], 0)}</div>
          <div className="ms-book-turn-shade" style={{ opacity: Math.sin(Math.PI * fraction) * .25 }} />
        </motion.div>}
      </motion.div>
    </div>
    <nav className="ms-book-navigation" aria-label="Preview pages">
      <Button icon="back" title="Previous spread" disabled={bounded === 0} onClick={() => turn(-1)} />
      <div aria-live="polite"><span>{current === 0 ? `1 of ${doc.pages.length}` : `${start + 1}–${start + pages.length} of ${doc.pages.length}`}</span></div>
      <Button icon="chevron" title={current === 0 ? "Open menu" : "Next spread"} disabled={bounded === last} onClick={() => turn(1)} />
    </nav>
  </section>;
}
